<template>
  <div>
    <v-row>
      <v-col>
        <v-card>
          <v-card-title>Details</v-card-title>
          <v-card-text>
            <v-row>
              <v-col>
                <p class="mb-1 text-caption">Product ID</p>
                <p class="display-1 accent--text">{{currentProduct.id}}</p>
              </v-col>
              <v-col>
                <p class="mb-1 text-caption">Product type</p>
                <p class="display-1 accent--text">{{kindNameMapping[currentProduct.product_kind.toLowerCase()]}}</p>
              </v-col>
              <v-col cols="2">
                <template v-if="!statusChangeOpen">
                  <p class="mb-1 text-caption">Status <v-icon small @click="openStatusChange()">edit</v-icon></p>
                  <p class="display-1 accent--text">{{currentProduct.status}}</p>
                </template>
                <template v-else>
                  <v-row>
                    <v-col cols="8"><v-select v-model="statusChange" :items="getProductStatuses" label="Select Status" dense outlined hide-details :disabled="statusesLoading||statusUpdateLoading" :loading="statusesLoading||statusUpdateLoading"></v-select></v-col>
                    <v-col cols="2" class="mt-2"><v-icon :disabled="statusUpdateLoading" :loading="statusUpdateLoading" color="secondary" @click="updateStatus">check</v-icon></v-col>
                    <v-col cols="2" class="mt-2"><v-icon :disabled="statusUpdateLoading" :loading="statusUpdateLoading" color="error" @click="statusChangeOpen=false;">close</v-icon></v-col>
                  </v-row>
                </template>
              </v-col>
              <v-col>
                <p class="mb-1 text-caption">Monthly Costs (exc. VAT)</p>
                <p class="display-1 accent--text">£{{currentProduct.promo_monthly_total|formatPrice}}</p>
              </v-col>
              <v-col>
                <p class="mb-1 text-caption">Sold date</p>
                <p><b>{{currentProduct.sold_date | formatDate | isEmpty}}</b></p>
              </v-col>

              <template v-if="currentProduct.confirmed_date">
                <v-col>
                  <p class="mb-1 text-caption">Live date</p>
                  <p><b>{{currentProduct.confirmed_date | formatDate | isEmpty}}</b></p>
                </v-col>
              </template>
              <template v-else>
                <v-col>

                  <template v-if="!fldChangeOpen">
                    <p class="mb-1 text-caption">Future live date <v-icon small @click="openFLD">edit</v-icon></p>
                    <p class="error--text"><b>{{currentProduct.future_live_date | formatDate | isEmpty}}</b></p>
                  </template>

                  <template v-else>
                    <v-row>
                      <div class="d-flex justify-start">

                        <div class="pr-2"><DateSelect v-model="future_live_date" label="Future live date" :disabled="changeFLDLoading" :loading="changeFLDLoading"/></div>
                        <div class="pr-4">
                          <div><v-icon small color="secondary" @click="changeFLD">check</v-icon></div>
                          <div><v-icon small color="error" @click="fldChangeOpen=false;">close</v-icon></div>
                        </div>

                      </div>
                    </v-row>
                  </template>

                </v-col>
              </template>

              <v-col>
                <template v-if="!editCeaseDateOpen">
                  <p class="mb-1 text-caption">Cease date <v-icon small v-if="currentProduct.status === 'Transfer Out'" @click="editCeaseDate()">edit</v-icon></p>
                  <p><b>{{currentProduct.ceased_date | formatDate | isEmpty}}</b></p>
                </template>
                <template v-else>
                  <v-row>
                    <v-col cols="12">
                      <v-dialog ref="editCeaseDateDialogOpen" v-model="editCeaseDateDialogOpen" :return-value.sync="ceaseDate" width="290px">
                        <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="ceaseDate" :allowed-dates="allowedDates" :disabled="ceaseDateUpdateLoading" :loading="ceaseDateUpdateLoading" label="Cease date" readonly v-bind="attrs" v-on="on" dense hide-details outlined></v-text-field>
                        </template>
                        <v-date-picker v-model="ceaseDate" :min="todaysDate" scrollable @input="$refs.editCeaseDateDialogOpen.save(ceaseDate);editCeaseDateDialogOpen=true;"></v-date-picker>
                      </v-dialog>
                    </v-col>
                    <v-col cols="6" class="mt-2"><v-icon :disabled="ceaseDateUpdateLoading" :loading="ceaseDateUpdateLoading" color="secondary" @click="updateCeaseDate">check</v-icon></v-col>
                    <v-col cols="6" class="mt-2"><v-icon :disabled="ceaseDateUpdateLoading" :loading="ceaseDateUpdateLoading" color="error" @click="editCeaseDateOpen=false;">close</v-icon></v-col>
                  </v-row>
                </template>
              </v-col>
              <v-col>
                <p class="mb-1 text-caption">Contract</p>
                <p><b>{{currentProduct | formatContractDate}}</b></p>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      
      <template v-if="(getProductIdLink && isTelco) || (getProductIdLink && isVoip)">
        <v-col cols="12" md="2">
          <v-card class="align-stretch" height="100%">
            <v-card-title>Linked Products</v-card-title>
            <v-card-text>
              <template v-for="(link,index) in getLinkedProducts">
                <div :key="`link_${index}`"><span class="font-weight-bold">{{link | linkType}}</span> - <span v-html="linkWording(link)"></span></div>
              </template>
            </v-card-text>
          </v-card>
        </v-col>

      </template>
    </v-row>
    
    <ProductStatusReasonDialog :dialogOpen="productStatusReasonDialog" :statusChange="statusChange" @close-dialog="closeProductStatusReasonDialog"/>
  </div>
</template>

<script>
  import { mapState,mapGetters,mapActions } from "vuex";

  import DateSelect from '@/components/Globals/DateSelect.vue';
  import ProductStatusReasonDialog from "@/components/Products/ProductTypes/AllProducts/ProductStatusReasonDialog";
  import moment from "moment";

  export default {

    components: { ProductStatusReasonDialog,DateSelect },

    computed: {
      ...mapState('products', ['currentProduct','statusesLoading','statusUpdateLoading']),
      ...mapGetters('products', ['getProductStatuses']),
      ...mapGetters('productLinks', ['getLinkedProducts']),

      isTelco() {
        if(Object.keys(this.currentProduct).length <= 0) return false;
        return this.currentProduct.product_kind.toLowerCase() === 'telco';
      },

      isVoip() {
        if(Object.keys(this.currentProduct).length <= 0) return false;
        return this.currentProduct.product_kind.toLowerCase() === 'voip';
      },

      productLink(){
        return (this.getLinkedProducts[0]!==undefined)?this.getLinkedProducts[0]:{}
      },

      getProductIdLink() {
        return (this.productLink.product_2_id === this.currentProduct.id ? this.productLink.product_1_id : this.productLink.product_2_id);
      },

      toOrFrom() {
        return (this.productLink.product_2_id === this.currentProduct.id ? 'from' : 'to');
      },

    },

    data() {
      return {
        kindNameMapping: {'web': 'Website','voip': 'VoIP','telco': 'Telco'},
        statusChangeOpen: false,
        editCeaseDateOpen: false,
        editCeaseDateDialogOpen: false,
        ceaseDateUpdateLoading: false,
        ceaseDate: null,
        todaysDate: moment(new Date()).format('YYYY-MM-DD'),
        statusChange: '',
        fldChange: '',
        fldChangeOpen: false,
        changeFLDLoading: false,
        future_live_date: '',
        statusesToChange: ['Option 1','Option 2'],
        productStatusReasonDialog: false,
      }
    },

    filters: {
      formatContractDate: (val=>{
        if(val.contract_start_date===null){
          return '-';
        }
        let startDate = moment(val.contract_start_date).format('YYYY-MM-DD');
        let endDate = moment(val.contract_start_date).add(val.contract_length_months,'M').format('YYYY-MM-DD');
        return `${startDate} to ${endDate}`;
      }),

      formatDate: (val=>{
        return (val!==null)?moment(val).format('YYYY-MM-DD'):'-';
      }),

      formatPrice: (val=>{
        return parseFloat(val).toFixed(2);
      }),

      isEmpty: (val=>{
        return (val!==undefined&&val!==null&&val!=='')?val:'-';
      }),

      linkType: (item)=>{
        return {
          BUNDLE: 'Bundle',
          COT: 'Transfer',
          COP: 'Transfer',
          INTERNAL_TRANSFER: 'Transfer',
        }[item.link_type] || 'Transfer';
      },
    },

    methods: {
      ...mapActions('products', ['fetchProductStatuses','updateProductStatuses','updateProduct','getProductById','updateProductData']),
      ...mapActions('productLinks', ['fetchLinkedProduct']),

      linkWording(item){
        const currentProductId = this.currentProduct.id;
        if(item.link_type==='BUNDLE'){
          const bundledWith = item.product_1_id === currentProductId ? item.product_2_id : item.product_1_id;
          return `Bundled with <a href="/products/${bundledWith}" target="_blank">${bundledWith}</a>`;
        }else{
          const fromTo = item.product_1_id === currentProductId ? 
            `to <a href="/products/${item.product_2_id}" target="_blank">${item.product_2_id}</a>` : 
            `from <a href="/products/${item.product_1_id}" target="_blank">${item.product_1_id}</a>`;
          const map = {
            COT: 'COT',
            COP: 'COP',
            INTERNAL_TRANSFER: 'Internal transfer',
          };
          return `Transferring ${fromTo} as ${map[item.link_type]}`;
        }
      },

      openFLD(){
        this.future_live_date = this.currentProduct.future_live_date;
        this.fldChangeOpen = true;
      },

      async changeFLD() {
        this.changeFLDLoading = true;
        let data = {future_live_date:(this.future_live_date===''||this.future_live_date===undefined)?null:this.future_live_date}
        await this.updateProductData({id:this.currentProduct.id,data:data});
        this.changeFLDLoading = false;
        this.fldChangeOpen = false;
      },

      async openStatusChange(){
        this.statusChangeOpen = true;
        await this.fetchProductStatuses(this.currentProduct.id);
      },

      async updateCeaseDate(){
        let data = {
          id: this.currentProduct.id,
          data: {
            ceased_date: this.ceaseDate
          }
        }
        let success = await this.updateProduct(data);
        if(success){
          this.editCeaseDateOpen = false;
          this.getProductById(this.currentProduct.id);
        }
      },

      allowedDates: val => moment(val) >= todaysDate,

      async editCeaseDate(){
        this.editCeaseDateOpen = true;
        this.editCeaseDateDialogOpen = true;
      },

      async updateStatus(){
        const statuses = ['DOA'];

        if(statuses.includes(this.statusChange)) {
          this.productStatusReasonDialog = true;
          return;
        }

        let data = {
          productId: this.currentProduct.id,
          status: {status: this.statusChange}
        }

        let success = await this.updateProductStatuses(data);
        if(success){
          this.statusChangeOpen = false;
          window.location.reload();
        }
      },

      closeProductStatusReasonDialog() {
        this.productStatusReasonDialog = false;
        this.statusChangeOpen = false;
      },
    },

    async mounted () {
      await this.fetchLinkedProduct(this.currentProduct.id);
    },
  }
</script>

<style lang="scss" scoped></style>
